import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getChatApiRoute, v1Client } from '../api/v1Client'
import { Shortcut } from '../types'
import { useAuth } from '../providers'

const USER_PROMPTS_QUERY_KEY = 'user-prompts'

export const useShortcuts = () => {
  const { getAuthToken } = useAuth()

  return useQuery({
    queryKey: [USER_PROMPTS_QUERY_KEY],
    queryFn: async (): Promise<Shortcut[]> => {
      const resp = await v1Client.GET('/shortcuts', {
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
        },
      })

      if (resp.error) {
        console.warn('Error fetching user prompts', resp.error)
        return []
      }

      const data = resp.data?.data ?? []

      return data as Shortcut[]
    },
    staleTime: 1000 * 60 * 30, // 30 minutes
  })
}

interface CreateShortcutInput {
  name: string
  prompt: string
}

export const useCreateShortcut = () => {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['create-shortcut'],
    mutationFn: async (shortcut: CreateShortcutInput): Promise<void> => {
      const token = await getAuthToken()
      const response = await fetch(getChatApiRoute('api/v1/shortcuts'), {
        method: 'POST',
        body: JSON.stringify({
          ...shortcut,
          public: false,
          description: '',
        }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
      if (!response.ok) {
        console.warn('Error creating shortcut', response)
        throw new Error('Failed to create shortcut')
      }
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USER_PROMPTS_QUERY_KEY] })
    },
  })
}

export const useShortcutById = (id?: string) => {
  const { getAuthToken } = useAuth()
  return useQuery({
    queryKey: ['shortcut', id],
    queryFn: async () => {
      const resp = await v1Client.GET(`/shortcuts/${id}`, {
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
        },
      })

      if (resp.error) {
        console.warn('Error fetching shortcut', resp.error)
        return null
      }

      return resp.data?.data as Shortcut
    },
    enabled: !!id,
  })
}

export const useUpdateShortcut = () => {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['update-shortcut'],
    mutationFn: async (shortcut: Shortcut): Promise<void> => {
      const token = await getAuthToken()
      await fetch(getChatApiRoute(`api/v1/shortcuts/${shortcut.id}`), {
        method: 'PATCH',
        body: JSON.stringify(shortcut),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USER_PROMPTS_QUERY_KEY] })
    },
  })
}

export const useDeleteShortcut = () => {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['delete-shortcut'],
    mutationFn: async (id: string): Promise<void> => {
      const token = await getAuthToken()
      await fetch(getChatApiRoute(`api/v1/shortcuts/${id}`), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USER_PROMPTS_QUERY_KEY] })
    },
  })
}
